import { createSelector } from 'reselect';
import { find } from 'lodash';

import {
    aggDataByDomainIdSelector,
    aggDataBySenderIdSelector,
} from '../../common/selectors/aggregate-results-selectors';
import { mySendersAggSelector } from '../../widgets/my-senders/my-senders-selectors';
import { recognizedSendersByIdSelector } from '../../common/selectors/recognized-senders-selectors';
import { rightPaneAttributesSelector, rightPaneTitleSelector } from '../../common/selectors/right-pane-selectors';
import { sendersByIdSelector } from '../../common/selectors/senders-selectors';

import { senderStatusSortValueMap } from '../../common/constants/sender-status';
import {
    SENDER_STATUS_ACCEPTED,
    SENDER_STATUS_UNDECIDED,
} from '../../common/constants/definitions';

function mapAggResults( sender ){
    return {
        ...sender,
        status: sender.verified ? SENDER_STATUS_ACCEPTED : SENDER_STATUS_UNDECIDED };
}

export const mapRecognizedSender = ( sender ) => {
    return {
        ...sender,
        id: sender.senderId
    };
};

function mapDomainToRecognizedSender( sender ){
    return {
        ...sender,
        id: sender.domain,
        name: sender.domain
    };
}

export const senderSortFunc = ( a, b ) => {
    if( senderStatusSortValueMap[a.status] < senderStatusSortValueMap[b.status] ){
        return -1;
    }

    if( senderStatusSortValueMap[a.status] > senderStatusSortValueMap[b.status] ){
        return 1;
    }

    if( senderStatusSortValueMap[a.status] === senderStatusSortValueMap[b.status] ){
        if( a.name.toLowerCase() < b.name.toLowerCase() ){
            return -1;
        }
        if( a.name.toLowerCase() > b.name.toLowerCase() ){
            return 1;
        }
        return 0;
    }

    return 0;
};

export const sendersDataByDomain = createSelector(
    aggDataByDomainIdSelector,
    recognizedSendersByIdSelector,
    rightPaneTitleSelector,
    ( aggResultsByDomainId, recognizedSendersById, selectedDomain ) => {
        if( !selectedDomain ){
            return [];
        }

        let { countsBySender = {} } = aggResultsByDomainId[selectedDomain] || {};
        const recognizedSendersMap = recognizedSendersById[selectedDomain] || {};

        // remove unknown senders
        delete countsBySender.null;

        const senders = Object.keys( countsBySender ).map( senderId => {
            // Data about recognized sender overrides data from agg results
            if( recognizedSendersMap[senderId] ){
                return mapRecognizedSender( recognizedSendersMap[senderId] );
            }

            return mapAggResults( countsBySender[senderId].sender );
        } );

        const sortedSenders = senders.sort( senderSortFunc );

        return sortedSenders;
    }
);

export const domainsDataBySender = createSelector(
    aggDataBySenderIdSelector,
    recognizedSendersByIdSelector,
    rightPaneAttributesSelector,
    ( aggResultsBySenderId, recognizedSendersById, rightPaneAttributes ) => {
        const selectedSender = rightPaneAttributes.senderId;

        if( !selectedSender ){
            return [];
        }

        let { countsByDomain = {} } = aggResultsBySenderId[selectedSender] || {};
        const recognizedSendersMap = recognizedSendersById[selectedSender] || {};

        const domains = Object.keys( countsByDomain ).map( domain => {
            // Data about recognized sender overrides data from agg results
            if( recognizedSendersMap[domain] ){
                return mapDomainToRecognizedSender( { ...countsByDomain[domain], ...recognizedSendersMap[domain] } );
            }

            return mapAggResults( {
                ...countsByDomain[domain],
                id: domain,
                name: domain
            } );
        } );

        const sortedDomains = domains.sort( senderSortFunc );

        return sortedDomains;
    }
);

export const mySenderProfileDataSelector = createSelector(
    sendersByIdSelector,
    rightPaneAttributesSelector,
    ( sendersByIdSelector, rightPaneAttributes ) => {
        return sendersByIdSelector[rightPaneAttributes.senderId];
    }
);

export const mySenderPaneDataSelector = createSelector(
    mySendersAggSelector,
    rightPaneAttributesSelector,
    ( mySendersAggData, rightPaneAttributes ) => {
        return find( mySendersAggData, sender => sender.senderId === rightPaneAttributes.senderId );
    }
);
